/**
 * API client for ChenMed in-session protocol nudges.
 *
 * The card view carries the protocol's structural item tree (a discriminated union
 * on `kind`) plus a separate `item_states` map of checked state, and a structured
 * `trigger` (named signals + verbatim quotes). Conditional answers (cond/fork/branch)
 * are ephemeral client-side state and are not part of the payload.
 */

import { getScribeHttpClients } from '@heidi/api/client';

const scribeHttpV2 = () => getScribeHttpClients().scribeHttpV2;

export type NudgeItemKind = 'check' | 'info' | 'choice' | 'cond' | 'fork' | 'branch';
export type ChoiceTag = 'first_line' | 'alternative';

export interface ChoiceOptionView {
  item_id: string;
  text: string;
  detail?: string | null;
  tag?: ChoiceTag | null;
}

export interface ChoiceOverrideView {
  cond_id: string;
  question: string;
  hint?: string | null;
  option: ChoiceOptionView;
}

/** A plain checkable order/instruction. */
export interface CheckItemView {
  kind: 'check';
  item_id: string;
  text: string;
  detail?: string | null;
  ctx?: string | null;
}

/** A non-checkable guidance note. */
export interface InfoItemView {
  kind: 'info';
  item_id: string;
  text: string;
}

/** CHOOSE ONE — radio-select; an `override` condition can supersede the whole list. */
export interface ChoiceItemView {
  kind: 'choice';
  item_id: string;
  label: string;
  options: ChoiceOptionView[];
  override?: ChoiceOverrideView | null;
}

export interface CondAutoView {
  sources: string[];
  label: string;
}

/** IF — a yes/no question gating nested items. `auto` resolves from checked sources. */
export interface CondItemView {
  kind: 'cond';
  item_id: string;
  question: string;
  hint?: string | null;
  when: 'yes' | 'no';
  auto?: CondAutoView | null;
  items: NudgeItem[];
}

export interface ForkArmView {
  items: NudgeItem[];
}

/** DECISION — one question routes to one of two arms. */
export interface ForkItemView {
  kind: 'fork';
  item_id: string;
  question: string;
  hint?: string | null;
  yes: ForkArmView;
  no: ForkArmView;
}

export interface BranchOptionView {
  option_id: string;
  label: string;
  items: NudgeItem[];
}

/** DEPENDS ON — value fork; exactly one option applies. */
export interface BranchItemView {
  kind: 'branch';
  item_id: string;
  label: string;
  hint?: string | null;
  options: BranchOptionView[];
}

export type NudgeItem =
  | CheckItemView
  | InfoItemView
  | ChoiceItemView
  | CondItemView
  | ForkItemView
  | BranchItemView;

export interface NudgeSectionView {
  section_id: string;
  label: string;
  items: NudgeItem[];
}

export interface TriggerQuoteView {
  text: string;
}

export interface TriggerView {
  matched: string[];
  quotes: TriggerQuoteView[];
}

export interface ItemStateView {
  checked: boolean;
  checked_at?: string | null;
  /** Set when this item's order has been folded into the generated note (reconciled to
   *  the checked set on each successful incorporate). The client diffs this against the
   *  current selection to know what to un-weave — so "what was applied" comes from durable
   *  state, not by re-parsing the clinician-context blob. Optional/additive. */
  applied_at?: string | null;
}

/**
 * Advisory, transcript-derived highlight for one item/branch. Rendered as a "suggested"
 * visual + hover rationale; it never implies the item is checked or the branch answered.
 * `kind: 'item_action'` targets a checkable item; `kind: 'branch_answer'` targets a
 * decision (`suggest_value` = 'yes'|'no' for a fork/cond, or an option_id for a branch).
 */
export interface ItemSuggestionView {
  item_id: string;
  kind: 'item_action' | 'branch_answer';
  suggest_value?: string | null;
  quote: string;
  rationale: string;
  confidence: number;
}

export interface SpecialNoteView {
  kind: string;
  text: string;
  position: string;
}

export type NudgeCardSource = 'auto' | 'manual';

export interface NudgeCardView {
  protocol_id: string;
  display_name: string;
  summary?: string | null;
  catalogue_version: string;
  status: 'surfaced' | 'dismissed';
  source: NudgeCardSource;
  surfaced_at: string;
  dismissed_at?: string | null;
  sections: NudgeSectionView[];
  special_notes: SpecialNoteView[];
  trigger: TriggerView;
  /** Checked state of every checkable leaf, keyed by item_id. */
  item_states: Record<string, ItemStateView>;
  /** Advisory transcript-derived suggestions (highlights), refreshed by the suggestion
   *  pass. Optional/additive — absent on older payloads; never implies "checked". */
  suggestions?: ItemSuggestionView[];
  /** Flattened single-string evidence, retained for back-compat. */
  trigger_evidence: string;
}

export interface NudgeListResponse {
  cards: NudgeCardView[];
}

export interface ProtocolSummaryView {
  protocol_id: string;
  display_name: string;
  summary?: string | null;
  version: string;
}

export interface ProtocolListResponse {
  protocols: ProtocolSummaryView[];
}

export interface DetectNudgesRequest {
  transcript_chunk: string;
  visit_summary?: string | null;
  is_session_end?: boolean;
}

/**
 * At-add context for a manual protocol add: the transcript chunk + visit summary the
 * clinician was looking at. Persisted server-side inside the PHI boundary as the
 * "what the model apparently missed" false-negative record, and hashed for analytics —
 * the raw text is never sent to Snowplow. Only `addNudge` carries this; interaction
 * events (dismiss / reopen / item toggle) derive their analytics from the stored card.
 */
export interface InteractionContext {
  visit_summary?: string | null;
  transcript_chunk?: string | null;
}

const BASE = '/in-session-guidance/sessions';

/** @domain REVIEW */
export async function listNudges(sessionId: string): Promise<NudgeListResponse> {
  const { data } = await scribeHttpV2().get<NudgeListResponse>(
    `${BASE}/${sessionId}/nudges`
  );
  return data;
}

/** @domain REVIEW */
export async function listProtocols(sessionId: string): Promise<ProtocolListResponse> {
  const { data } = await scribeHttpV2().get<ProtocolListResponse>(
    `${BASE}/${sessionId}/protocols`
  );
  return data;
}

/** @domain REVIEW */
export async function detectNudges(
  sessionId: string,
  request: DetectNudgesRequest
): Promise<NudgeListResponse> {
  const { data } = await scribeHttpV2().post<NudgeListResponse>(
    `${BASE}/${sessionId}/nudges/detect`,
    request
  );
  return data;
}

/**
 * Dedicated, accuracy-first pass that refreshes advisory item/branch suggestions on the
 * session's active cards. Runs off the detect critical path so card surfacing stays
 * snappy. Returns the full card list with `suggestions` populated.
 * @domain REVIEW
 */
export async function suggestNudges(
  sessionId: string,
  request: { transcript_chunk: string; visit_summary?: string | null }
): Promise<NudgeListResponse> {
  const { data } = await scribeHttpV2().post<NudgeListResponse>(
    `${BASE}/${sessionId}/nudges/suggest`,
    request
  );
  return data;
}

export interface IncorporateOrdersResponse {
  note: string;
  content_type: string;
}

/**
 * Stable TanStack mutation key for the incorporate-orders reconcile. Exported so surfaces
 * outside the nudge panel (the note editor) can observe "a reconcile is in flight" via
 * `useIsMutating({ mutationKey })` and lock editing for its (multi-second) duration —
 * complementing the server-side optimistic-concurrency guard on the write itself.
 */

/**
 * Reconciles an ALREADY-GENERATED note's orders to the clinician's current selection with
 * minimal edits (manual edits preserved server-side via edited_note). `orders` = the
 * orders the note should now reflect; `removed` = orders previously folded in that are now
 * deselected (passed explicitly so the model can un-weave them). Returns the updated note.
 * Only call when a note exists — otherwise route the orders to context. (Applied-state
 * tracking is a separate call — see `markOrdersApplied`.)
 * @domain REVIEW
 */
export async function incorporateOrders(
  sessionId: string,
  body: { orders: string[]; removed: string[] }
): Promise<IncorporateOrdersResponse> {
  const { data } = await scribeHttpV2().post<IncorporateOrdersResponse>(
    `${BASE}/${sessionId}/note/incorporate-orders`,
    body
  );
  return data;
}

/**
 * Records which protocol orders are currently materialized in the clinician-context orders
 * block, by reconciling each item's durable `applied_at` to exactly `itemIds`. Called once an
 * apply is committed to where it lands — no note, or after the note reconcile succeeds — so
 * the client can maintain that block by what WE wrote — this
 * durable per-session set — rather than by catalogue text, which can't tell our orders from
 * clinician-typed or pasted-from-a-prior-visit content. Distinct from `incorporateOrders`,
 * which un-weaves deselected orders from an existing note.
 * @domain REVIEW
 */
export async function markOrdersApplied(
  sessionId: string,
  itemIds: string[]
): Promise<void> {
  await scribeHttpV2().post(`${BASE}/${sessionId}/nudges/applied`, {
    applied_item_ids: itemIds,
  });
}

/**
 * Generates clinical tasks for a nudge session, steering the model away from protocol
 * actions the clinician already ticked off so handled items don't resurface as phantom
 * "open" tasks. The server persists the tasks (204); refetch the clinical-tasks query to
 * display them. Used in place of the background autogen for ChenMed nudge sessions.
 * @domain REVIEW
 */
export async function generateTasksExcludingCompleted(sessionId: string): Promise<void> {
  await scribeHttpV2().post(`${BASE}/${sessionId}/tasks/generate`);
}

/** @domain REVIEW */
export async function addNudge(
  sessionId: string,
  protocolId: string,
  context?: InteractionContext
): Promise<NudgeCardView> {
  const { data } = await scribeHttpV2().post<NudgeCardView>(
    `${BASE}/${sessionId}/nudges/add`,
    {
      protocol_id: protocolId,
      ...context,
    }
  );
  return data;
}

/** @domain REVIEW */
export async function patchNudgeCardStatus(
  sessionId: string,
  protocolId: string,
  status: 'surfaced' | 'dismissed',
  opts?: { reason?: string | null }
): Promise<NudgeCardView> {
  const { data } = await scribeHttpV2().patch<NudgeCardView>(
    `${BASE}/${sessionId}/nudges/${protocolId}`,
    { status, reason: opts?.reason }
  );
  return data;
}

/** @domain REVIEW */
export async function patchNudgeItemChecked(
  sessionId: string,
  protocolId: string,
  itemId: string,
  checked: boolean
): Promise<NudgeCardView> {
  const { data } = await scribeHttpV2().patch<NudgeCardView>(
    `${BASE}/${sessionId}/nudges/${protocolId}/items/${encodeURIComponent(itemId)}`,
    { checked }
  );
  return data;
}
